import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, Chip, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import { useCompanyPriceEod } from "../Charts/hooks/useCompanyPriceEod";
import { useCompanyPrice30Min } from "../Charts/hooks/useCompanyPrice30Min";
import { useCompanyPrice4Hour } from "../Charts/hooks/useCompanyPrice4Hour";
import ChartTooltip from "../Charts/components/shared/ChartTooltip";

type Period = "1M" | "3M" | "6M" | "1Y" | "3Y" | "5Y" | "10Y";

const PERIODS: Period[] = ["1M", "3M", "6M", "1Y", "3Y", "5Y", "10Y"];

const periodToDays: Record<Period, number> = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
  "3Y": 1095,
  "5Y": 1825,
  "10Y": 3650,
};

type Resolution = "30min" | "4hour" | "eod";

const getResolution = (p: Period): Resolution => {
  if (p === "1M") return "30min";
  if (p === "3M" || p === "6M") return "4hour";
  return "eod";
};

// Extract just the YYYY-MM-DD part from a date or datetime string
const datePart = (dateStr: string): string => dateStr.split(" ")[0];

interface BasePriceRecord {
  date: string;
  close: number;
}

interface BasePriceData {
  data: BasePriceRecord[];
}

const SPY_ID = "S&P 500";

const getMobileLabel = (id: string) => {
  if (id === SPY_ID) return "S&P";
  return id.split(".")[0];
};

interface ComparisonPriceChartProps {
  ticker1?: string;
  ticker2?: string;
  color1?: string;
  color2?: string;
}

const ComparisonPriceChart = ({
  ticker1,
  ticker2,
  color1 = "#22c55e",
  color2 = "#3b82f6",
}: ComparisonPriceChartProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [period, setPeriod] = useState<Period>("1Y");
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Unique DOM id so opacity can be toggled via direct DOM manipulation (no re-render)
  const tooltipGroupId = useMemo(
    () => `compare-tooltip-${Math.random().toString(36).slice(2, 11)}`,
    [],
  );

  // Persist last valid slice so the tooltip doesn't vanish on dead spots between
  // categorical point-scale positions (mirrors StockPriceChart's approach)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lastSliceRef = useRef<any>(null);

  // Clear stale slice when the period changes so old data isn't shown briefly
  const prevPeriodRef = useRef<Period>(period);
  if (prevPeriodRef.current !== period) {
    lastSliceRef.current = null;
    prevPeriodRef.current = period;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [mobileTooltip, setMobileTooltip] = useState<{ slice: any; x: number; y: number } | null>(null);
  const mobileTouchOriginRef = useRef<{ y: number } | null>(null);

  useEffect(() => {
    if (!isMobile) return;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) mobileTouchOriginRef.current = { y: t.clientY };
      if (!chartContainerRef.current?.contains(e.target as Node)) {
        setMobileTooltip(null);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !mobileTouchOriginRef.current) return;
      if (Math.abs(t.clientY - mobileTouchOriginRef.current.y) > 10) {
        setMobileTooltip(null);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true, capture: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart, { capture: true });
      window.removeEventListener("touchmove", onTouchMove, { capture: true });
    };
  }, [isMobile]);

  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
    () => new Set([ticker1, ticker2, SPY_ID].filter(Boolean) as string[]),
  );

  useEffect(() => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (ticker1) next.add(ticker1);
      if (ticker2) next.add(ticker2);
      next.add(SPY_ID);
      return next;
    });
  }, [ticker1, ticker2]);

  const toggleSeries = (id: string) => {
    setVisibleSeries((prev) => {
      if (prev.has(id) && prev.size <= 1) return prev;
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const resolution = getResolution(period);
  const today = dayjs().format("YYYY-MM-DD");
  const from = dayjs().subtract(periodToDays[period], "day").format("YYYY-MM-DD");

  // 30-min hooks — active only for 1M period
  const { data: data1_30min, isLoading: isLoading1_30min } = useCompanyPrice30Min(
    resolution === "30min" ? ticker1 : undefined, from, today,
  );
  const { data: data2_30min, isLoading: isLoading2_30min } = useCompanyPrice30Min(
    resolution === "30min" ? ticker2 : undefined, from, today,
  );
  const { data: dataSpy_30min } = useCompanyPrice30Min(
    resolution === "30min" ? "SPY" : undefined, from, today,
  );

  // 4-hour hooks — active for 3M and 6M periods
  const { data: data1_4hour, isLoading: isLoading1_4hour } = useCompanyPrice4Hour(
    resolution === "4hour" ? ticker1 : undefined, from, today,
  );
  const { data: data2_4hour, isLoading: isLoading2_4hour } = useCompanyPrice4Hour(
    resolution === "4hour" ? ticker2 : undefined, from, today,
  );
  const { data: dataSpy_4hour } = useCompanyPrice4Hour(
    resolution === "4hour" ? "SPY" : undefined, from, today,
  );

  // EOD hooks — active for 1Y, 3Y, 5Y, 10Y periods
  const { data: data1_eod, isLoading: isLoading1_eod } = useCompanyPriceEod(
    resolution === "eod" ? ticker1 : undefined, from, today,
  );
  const { data: data2_eod, isLoading: isLoading2_eod } = useCompanyPriceEod(
    resolution === "eod" ? ticker2 : undefined, from, today,
  );
  const { data: dataSpy_eod } = useCompanyPriceEod(
    resolution === "eod" ? "SPY" : undefined, from, today,
  );

  // Select the active dataset based on current resolution
  const data1 = (resolution === "30min" ? data1_30min : resolution === "4hour" ? data1_4hour : data1_eod) as BasePriceData | undefined;
  const data2 = (resolution === "30min" ? data2_30min : resolution === "4hour" ? data2_4hour : data2_eod) as BasePriceData | undefined;
  const dataSpy = (resolution === "30min" ? dataSpy_30min : resolution === "4hour" ? dataSpy_4hour : dataSpy_eod) as BasePriceData | undefined;
  const isLoading1 = resolution === "30min" ? isLoading1_30min : resolution === "4hour" ? isLoading1_4hour : isLoading1_eod;
  const isLoading2 = resolution === "30min" ? isLoading2_30min : resolution === "4hour" ? isLoading2_4hour : isLoading2_eod;

  const isLoading = (!!ticker1 && isLoading1) || (!!ticker2 && isLoading2);

  // Build normalised series aligned to the common start date across all series.
  // The common start is the latest first-date among all present series, ensuring that
  // a newly-listed stock doesn't produce a mismatched zero baseline for the others.
  const allSeries = useMemo(() => {
    const raw1 = ticker1 && data1?.data?.length ? data1.data : null;
    const raw2 = ticker2 && data2?.data?.length ? data2.data : null;
    const rawSpy = dataSpy?.data?.length ? dataSpy.data : null;

    // Collect each series' earliest date (date part only, for cross-resolution consistency)
    const firstDates = [raw1, raw2, rawSpy]
      .filter((d): d is BasePriceRecord[] => d !== null)
      .map((d) => datePart(d[0].date));

    // Align to the latest start date so all series share the same zero point
    const commonStart = firstDates.length > 0
      ? firstDates.reduce((max, d) => (d > max ? d : max), firstDates[0])
      : null;

    const alignedSlice = (data: BasePriceRecord[]) =>
      commonStart ? data.filter((d) => datePart(d.date) >= commonStart) : data;

    const series: { id: string; color: string; data: { x: string; y: number }[] }[] = [];

    if (ticker1 && raw1) {
      const filtered = alignedSlice(raw1);
      const base = filtered[0]?.close;
      if (base != null) {
        series.push({
          id: ticker1,
          color: color1 ?? "#22c55e",
          data: filtered.map((d) => ({
            x: d.date,
            y: parseFloat((((d.close - base) / base) * 100).toFixed(2)),
          })),
        });
      }
    }

    if (ticker2 && raw2) {
      const filtered = alignedSlice(raw2);
      const base = filtered[0]?.close;
      if (base != null) {
        series.push({
          id: ticker2,
          color: color2 ?? "#3b82f6",
          data: filtered.map((d) => ({
            x: d.date,
            y: parseFloat((((d.close - base) / base) * 100).toFixed(2)),
          })),
        });
      }
    }

    if (rawSpy) {
      const filtered = alignedSlice(rawSpy);
      const base = filtered[0]?.close;
      if (base != null) {
        series.push({
          id: SPY_ID,
          color: "#94a3b8",
          data: filtered.map((d) => ({
            x: d.date,
            y: parseFloat((((d.close - base) / base) * 100).toFixed(2)),
          })),
        });
      }
    }

    return series.length > 0 ? series : null;
  }, [data1, data2, dataSpy, ticker1, ticker2, color1, color2]);

  const chartData = useMemo(
    () => allSeries?.filter((s) => visibleSeries.has(s.id)) ?? null,
    [allSeries, visibleSeries],
  );

  // Evenly-spaced tick values derived from the first visible series' x values
  const axisTickValues = useMemo(() => {
    if (!chartData || !chartData[0]?.data.length) return undefined;
    const data = chartData[0].data;
    const targetCount = isXs ? 4 : 5;
    if (data.length <= targetCount) return data.map((d) => d.x);
    const step = Math.ceil(data.length / targetCount);
    return data.filter((_, i) => i % step === 0).map((d) => d.x);
  }, [chartData, isXs]);

  // Period-aware axis label — mirrors StockPriceChart formatting conventions
  const formatAxisLabel = useCallback((value: string): string => {
    if (period === "5Y" || period === "10Y") return dayjs(value).format("YYYY");
    if (period === "1Y" || period === "3Y") {
      const d = dayjs(value);
      return `${d.format("MMM")} '${d.format("YY")}`;
    }
    if (period === "6M") return dayjs(value).format("MMM");
    return dayjs(value).format("MMM D"); // 1M, 3M
  }, [period]);

  const border = isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0";
  const bg = isDark ? "#1a1a1a" : "#ffffff";
  const textPrimary = isDark ? "#e4e6ea" : "#0f172a";
  const textMuted = isDark ? "#6b7280" : "#94a3b8";
  const colors = chartData ? chartData.map((s) => s.color) : [color1, color2];

  // Zero reference line at y=0
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const zeroLineLayer = useCallback(({ yScale, innerWidth }: any) => {
    const y = (yScale as (v: number) => number)(0);
    if (y === undefined) return null;
    return (
      <line
        x1={0}
        x2={innerWidth}
        y1={y}
        y2={y}
        stroke={isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}
        strokeWidth={1}
        strokeDasharray="4 4"
      />
    );
  }, [isDark]);

  // Custom desktop tooltip layer with boundary-clamped ChartTooltip + crosshair.
  // The <g> is always present in the DOM at opacity 0 — visibility is toggled via
  // onMouseEnter/onMouseLeave using direct DOM manipulation (no React re-render).
  // lastSliceRef ensures the tooltip holds its last position instead of vanishing
  // when the cursor briefly lands on a dead spot between categorical point positions.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tooltipLayer = useCallback(({ currentSlice, innerWidth, innerHeight }: any) => {
    // On mobile the tap-based portal tooltip handles interaction instead
    if (isMobile) return null;

    if (currentSlice) {
      lastSliceRef.current = currentSlice;
    }
    const slice = currentSlice ?? lastSliceRef.current;

    const tooltipWidth = 190;
    const halfWidth = tooltipWidth / 2;
    const x = slice?.x ?? 0;
    const tooltipX = slice ? Math.max(halfWidth, Math.min(x, innerWidth - halfWidth)) : 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows = slice ? slice.points.map((pt: any) => ({
      label: String(pt.seriesId),
      value: `${(pt.data.y as number) >= 0 ? "+" : ""}${(pt.data.y as number).toFixed(2)}%`,
      color: pt.seriesColor,
    })) : [];
    const date = slice ? dayjs(slice.points[0].data.x as string).format("MMM D, YYYY") : "";

    return (
      <g id={tooltipGroupId} style={{ opacity: 0, transition: "opacity 0.1s ease-out" }}>
        {slice && (
          <>
            <line
              x1={x}
              x2={x}
              y1={0}
              y2={innerHeight}
              stroke={isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)"}
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            <foreignObject
              x={tooltipX - halfWidth}
              y={8}
              width={tooltipWidth}
              height={200}
              style={{ overflow: "visible", pointerEvents: "none" }}
            >
              <div>
                <ChartTooltip header={date} rows={rows} />
              </div>
            </foreignObject>
          </>
        )}
      </g>
    );
  }, [isDark, isMobile, tooltipGroupId]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const diagonalTick = useCallback((tick: any) => {
    const label = formatAxisLabel(tick.value as string);
    return (
      <g transform={`translate(${tick.x},${tick.y})`}>
        <text
          textAnchor="end"
          transform="rotate(-45)"
          dy={14}
          dx={-4}
          style={{
            fontSize: 10,
            fill: theme.palette.text.primary,
            fontWeight: 600,
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {label}
        </text>
      </g>
    );
  }, [theme.palette.text.primary, formatAxisLabel]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartLayers: any[] = useMemo(
    () => ["grid", "axes", zeroLineLayer, "lines", "points", "slices", "mesh", tooltipLayer],
    [zeroLineLayer, tooltipLayer],
  );

  return (
    <Box
      sx={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header: 1fr [title] | auto [series chips — true centre] | 1fr [period selector] */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.5, sm: 1.75 },
          pb: 0.5,
        }}
      >
        {/* Title — left */}
        <Typography
          sx={{
            fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            fontWeight: 700,
            color: textPrimary,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          Price Return
        </Typography>

        {/* Series toggle chips — geometrically centred (hidden on xs, shown below chart) */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, flexWrap: "wrap", justifyContent: "center", gap: 1 }}>
          {allSeries?.map((s) => {
            const isVisible = visibleSeries.has(s.id);
            return (
              <Chip
                key={s.id}
                label={s.id}
                onClick={() => toggleSeries(s.id)}
                variant={isVisible ? "filled" : "outlined"}
                sx={{
                  backgroundColor: isVisible ? s.color : "transparent",
                  color: isVisible ? "white" : s.color,
                  border: `1px solid ${s.color}`,
                  fontSize: "0.6875rem",
                  height: 24,
                  cursor: "pointer",
                  fontWeight: 600,
                  borderRadius: 0,
                  transition: "all 200ms linear",
                  "& .MuiChip-label": { px: 1.5 },
                  "&:hover": {
                    backgroundColor: isVisible
                      ? s.color
                      : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    borderColor: s.color,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Period selector — right (hidden on xs, shown below chart) */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {PERIODS.map((p) => {
            const isSelected = period === p;
            return (
              <Chip
                key={p}
                label={p}
                onClick={() => setPeriod(p)}
                sx={{
                  backgroundColor: isSelected
                    ? isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"
                    : "transparent",
                  color: isSelected ? "#22c55e" : textMuted,
                  border: `1px solid ${isSelected ? "rgba(34,197,94,0.3)" : border}`,
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                  height: { xs: 24, sm: 28 },
                  minWidth: { xs: 32, sm: 44 },
                  cursor: "pointer",
                  "& .MuiChip-label": { px: { xs: 0.5, sm: 1 } },
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Chart body */}
      <Box
        ref={chartContainerRef}
        onMouseEnter={() => {
          const el = document.getElementById(tooltipGroupId);
          if (el) el.style.opacity = "1";
        }}
        onMouseLeave={() => {
          const el = document.getElementById(tooltipGroupId);
          if (el) el.style.opacity = "0";
          lastSliceRef.current = null;
        }}
        sx={{
          height: { xs: 220, sm: 280, md: 320 },
          backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
          borderRadius: 1,
          mx: 2,
          my: 1.5,
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <CircularProgress size={28} sx={{ color: color1 }} />
          </Box>
        )}
        {!isLoading && !chartData && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: textMuted }}>
              No price data available
            </Typography>
          </Box>
        )}
        {!isLoading && chartData && (
          <ResponsiveLine
            data={chartData}
            theme={{
              background: "transparent",
              text: {
                fontSize: 12,
                fill: theme.palette.text.primary,
                outlineWidth: 0,
                outlineColor: "transparent",
              },
              axis: {
                domain: {
                  line: { stroke: "transparent", strokeWidth: 0 },
                },
                ticks: {
                  line: { stroke: "transparent", strokeWidth: 0 },
                  text: {
                    fontSize: 11,
                    fill: theme.palette.text.primary,
                    fontWeight: 600,
                  },
                },
              },
              grid: {
                line: {
                  stroke: theme.palette.divider,
                  strokeWidth: 1,
                  strokeOpacity: 0.2,
                },
              },
              tooltip: {
                container: {
                  background: "transparent",
                  border: "none",
                  borderRadius: 0,
                  boxShadow: "none",
                  padding: 0,
                },
              },
            }}
            colors={colors}
            margin={{ top: 20, right: 16, bottom: isXs ? 40 : 44, left: isXs ? 48 : 56 }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", stacked: false, min: "auto", max: "auto" }}
            curve="monotoneX"
            axisBottom={{
              format: formatAxisLabel,
              tickValues: axisTickValues,
              tickSize: 0,
              tickPadding: isXs ? 4 : 10,
              ...(isXs && { renderTick: diagonalTick }),
            }}
            axisLeft={{
              format: (v: number) => `${v > 0 ? "+" : ""}${v.toFixed(0)}%`,
              tickSize: 0,
              tickPadding: 10,
              tickValues: 5,
            }}
            enableGridX={false}
            enableGridY={true}
            enablePoints={false}
            enableCrosshair={false}
            enableSlices="x"
            sliceTooltip={() => null}
            lineWidth={3}
            layers={chartLayers}
            {...(isMobile && {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onClick: (slice: any, event: React.MouseEvent) => {
                event.stopPropagation();
                setMobileTooltip(prev =>
                  prev && String(prev.slice.id) === String(slice.id)
                    ? null
                    : { slice, x: event.clientX, y: event.clientY }
                );
              },
            })}
          />
        )}
      </Box>

      {isMobile && mobileTooltip && ReactDOM.createPortal(
        <div
          style={{
            position: "fixed",
            left: Math.max(148, Math.min(mobileTooltip.x, window.innerWidth - 148)),
            top: mobileTooltip.y - 24,
            transform: "translate(-50%, -100%)",
            zIndex: 99999,
            pointerEvents: "none",
          }}
        >
          {(() => {
            const date = dayjs(mobileTooltip.slice.points[0].data.x as string).format("MMM D, YYYY");
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows = mobileTooltip.slice.points.map((pt: any) => ({
              label: String(pt.seriesId),
              value: `${(pt.data.y as number) >= 0 ? "+" : ""}${(pt.data.y as number).toFixed(2)}%`,
              color: pt.seriesColor,
            }));
            return <ChartTooltip header={date} rows={rows} />;
          })()}
        </div>,
        document.body
      )}

      {/* Mobile controls row — series chips left, period chips right, xs only */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2,
          pb: 1.5,
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {allSeries?.map((s) => {
            const isVisible = visibleSeries.has(s.id);
            return (
              <Chip
                key={s.id}
                label={getMobileLabel(s.id)}
                onClick={() => toggleSeries(s.id)}
                variant={isVisible ? "filled" : "outlined"}
                sx={{
                  backgroundColor: isVisible ? s.color : "transparent",
                  color: isVisible ? "white" : s.color,
                  border: `1px solid ${s.color}`,
                  fontSize: "0.625rem",
                  height: 22,
                  cursor: "pointer",
                  fontWeight: 600,
                  borderRadius: 0,
                  transition: "all 200ms linear",
                  "& .MuiChip-label": { px: 1 },
                  "&:hover": {
                    backgroundColor: isVisible
                      ? s.color
                      : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    borderColor: s.color,
                  },
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end", flexShrink: 0, maxWidth: "55%" }}>
          {PERIODS.map((p) => {
            const isSelected = period === p;
            return (
              <Chip
                key={p}
                label={p}
                onClick={() => setPeriod(p)}
                sx={{
                  backgroundColor: isSelected
                    ? isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"
                    : "transparent",
                  color: isSelected ? "#22c55e" : textMuted,
                  border: `1px solid ${isSelected ? "rgba(34,197,94,0.3)" : border}`,
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: "0.625rem",
                  height: 22,
                  minWidth: 28,
                  cursor: "pointer",
                  "& .MuiChip-label": { px: 0.5 },
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ComparisonPriceChart;
